'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Affix, Center, Grid, Space, Text, Transition } from '@mantine/core'
import { IconSearch, IconShoppingCart } from '@tabler/icons-react'
import { useRouter } from 'next/navigation'
import { createSerializer, parseAsFloat, parseAsJson, parseAsString, useQueryStates } from 'nuqs'
import { cartSchema } from '../_helpers/cartSchema'
import { ProductCard } from './ProductCard'

interface Product {
	rating: number | null | undefined
	category: number | null | undefined
	popularity: number | null | undefined
	what: string | null | undefined
	quantity: number | null | undefined
	howMuch: number | null | undefined
	image: string | null | undefined
	id: number
}

interface GiftListProps {
	products: Product[]
	utm: string
	min: number
	max: number
}

export function GiftList({ products, min, max, utm }: GiftListProps) {
	const searchParamsSchema = {
		carrinho: parseAsJson(cartSchema.parse).withDefault({}),
		valor: parseAsFloat.withDefault(max),
		busca: parseAsString.withDefault(''),
	}

	const serialize = createSerializer(searchParamsSchema)

	const [searchParams, setSearchParams] = useQueryStates(searchParamsSchema)
	const { carrinho, valor, busca } = searchParams

	const { push } = useRouter()

	const filteredProducts = products.filter(
		product => (product.howMuch ?? 0) <= valor && (product.what ?? '').toLowerCase().includes(busca.toLowerCase()),
	)

	const serializedUrl = serialize(searchParams)

	return (
		<div className="container mx-auto px-4">
			<Space h={20} />
			<Center>
				<div className="w-full sm:w-2/3 md:w-1/2 lg:w-2/5 xl:w-1/3">
					<Input
						placeholder="Pesquisar"
						className="text-center rounded-full border[1px] border-black w-full text-lg"
						rightSection={<IconSearch size={16} className="text-gray-500 ml-3" />}
						value={busca}
						onChange={e => setSearchParams({ busca: e.target.value })}
					/>
				</div>
			</Center>
			<Space h={50} />

			<div className="w-full ">
				<div className="flex justify-between mb-4">
					<Text size="lg" fw={500} className="text-gray-600">
						Preço máximo:
					</Text>
				</div>
				<Slider
					defaultValue={[valor]}
					min={min}
					max={max}
					step={1}
					onValueChange={values => setSearchParams({ valor: values[0] })}
					className="w-full h-8"
				/>
			</div>
			<Space h={50} />
			<Grid gutter={{ base: 20, sm: 30, md: 50, lg: 60 }}>
				{filteredProducts.map(product => {
					const { id } = product
					const cartQuantity = carrinho[id] || 0
					const availableQuantity = product.quantity || 0
					const canIncrement = cartQuantity < availableQuantity

					const incrementQuantity = () => {
						if (canIncrement) {
							setSearchParams({ carrinho: { ...carrinho, [id]: cartQuantity + 1 } })
						}
					}

					const decrementQuantity = () => {
						if (cartQuantity > 0) {
							const newQuantity = cartQuantity - 1
							setSearchParams({
								carrinho: newQuantity === 0 ? { ...carrinho, [id]: undefined } : { ...carrinho, [id]: newQuantity },
							})
						}
					}

					return (
						<ProductCard
							key={`products-${id}`}
							product={product}
							cartQuantity={cartQuantity}
							onIncrement={incrementQuantity}
							onDecrement={decrementQuantity}
						/>
					)
				})}
			</Grid>
			<Affix position={{ bottom: 20, right: 20 }}>
				<Transition transition="slide-up" mounted={Object.values(carrinho).some(qty => (qty || 0) > 0)}>
					{transitionStyles => (
						<Button
							onClick={() => push(`/${utm}/checkout${serializedUrl}`)}
							className="flex shadow-lg bg-[linear-gradient(110deg,#cbadca,45%,#d59fd3,55%,#cbadca)] text-white bg-[linear-gradient(110deg,#A56AAB,45%,#d59fd3,55%,#A56AAB)]"
							style={{ padding: '12px 24px' }}
						>
							<IconShoppingCart size={24} className="mr-2" />
							Presentear ({Object.values(carrinho).reduce((sum: number, qty) => sum + (qty || 0), 0)} itens)
						</Button>
					)}
				</Transition>
			</Affix>
			<Space h={80} />
		</div>
	)
}
